import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LensInclusion,
  BarcodeLookupResult,
  BrandSummary,
  LabelRequest,
  LabelSheet,
  PaginatedResult,
  ProductBarcodeSummary,
  ProductDetail,
  ProductMediaRole,
  ProductMediaSummary,
  ProductStatus,
  ProductSummary,
  ProductType,
  ProductVariantSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';

export interface ProductListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: ProductType | '';
  status?: ProductStatus | '';
  brandId?: string;
  categoryId?: string;
  lensInclusion?: LensInclusion | '';
}

export interface VariantPayload {
  sku?: string;
  isBatchTracked?: boolean;
  variantName?: string;
  costPriceMinor?: number;
  sellingPriceMinor?: number;
  isActive?: boolean;
  barcode?: string;
  barcodeType?: string;
  frame?: Record<string, unknown>;
  prescriptionLens?: Record<string, unknown>;
  contactLens?: Record<string, unknown>;
  solution?: Record<string, unknown>;
}

export interface ProductPayload {
  type?: ProductType;
  name?: string;
  nameAr?: string;
  description?: string;
  brandId?: string;
  /** Tenant-defined merchandising grouping, independent of `type`. */
  categoryId?: string;
  /** Frame and sunglasses only. */
  lensInclusion?: LensInclusion;
  variants?: VariantPayload[];
}

export const catalogKeys = {
  all: ['catalog'] as const,
  brands: () => [...catalogKeys.all, 'brands'] as const,
  products: () => [...catalogKeys.all, 'products'] as const,
  productList: (params: ProductListParams) => [...catalogKeys.products(), 'list', params] as const,
  product: (id: string) => [...catalogKeys.products(), 'detail', id] as const,
  barcodes: (variantId: string) => [...catalogKeys.all, 'barcodes', variantId] as const,
};

/** Strips empty values so the API never receives `type=` or `search=`. */
function toParams(params: ProductListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  };
  if (params.search?.trim()) out.search = params.search.trim();
  if (params.type) out.type = params.type;
  if (params.status) out.status = params.status;
  if (params.brandId) out.brandId = params.brandId;
  if (params.categoryId) out.categoryId = params.categoryId;
  if (params.lensInclusion) out.lensInclusion = params.lensInclusion;
  return out;
}

export function useProductsQuery(params: ProductListParams) {
  return useQuery({
    queryKey: catalogKeys.productList(params),
    queryFn: () =>
      api.get<PaginatedResult<ProductSummary>>('/products', { params: toParams(params) }),
    // Keeps the previous page on screen while the next loads, so the table does not flash empty.
    placeholderData: (previous) => previous,
  });
}

/** The same query as `useProductQuery`, for fetching a product on demand from an event handler. */
export function productQueryOptions(id: string) {
  return {
    queryKey: catalogKeys.product(id),
    queryFn: () => api.get<ProductDetail>(`/products/${id}`),
  };
}

export function useProductQuery(id: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.product(id ?? ''),
    queryFn: () => api.get<ProductDetail>(`/products/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useBrandsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: catalogKeys.brands(),
    queryFn: () =>
      api.get<PaginatedResult<BrandSummary>>('/brands', {
        params: { pageSize: 100, isActive: true },
      }),
    // Brands change rarely; refetching them on every product form open is wasted work.
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useCreateBrandMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.post<BrandSummary>('/brands', { name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.brands() });
    },
  });
}

export function useCreateProductMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProductPayload) => api.post<ProductDetail>('/products', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

export function useUpdateProductMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProductPayload) => api.patch<ProductDetail>(`/products/${id}`, payload),
    onSuccess: (product) => {
      queryClient.setQueryData(catalogKeys.product(id), product);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

export function useChangeProductStatusMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { status: ProductStatus; reason?: string }) =>
      api.patch<ProductDetail>(`/products/${id}/status`, payload),
    onSuccess: (product) => {
      queryClient.setQueryData(catalogKeys.product(id), product);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

export function useAddVariantMutation(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VariantPayload) =>
      api.post<ProductVariantSummary>(`/products/${productId}/variants`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

export function useUpdateVariantMutation(productId: string, variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VariantPayload) =>
      api.patch<ProductVariantSummary>(`/variants/${variantId}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

export function useAddBarcodeMutation(productId: string, variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { code: string; type?: string; isPrimary?: boolean }) =>
      api.post<ProductBarcodeSummary>(`/variants/${variantId}/barcodes`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
    },
  });
}

export function useMakeBarcodePrimaryMutation(productId: string, variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (barcodeId: string) =>
      api.patch<ProductBarcodeSummary[]>(`/variants/${variantId}/barcodes/${barcodeId}/primary`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
    },
  });
}

export function useRemoveBarcodeMutation(productId: string, variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (barcodeId: string) =>
      api.delete<ProductBarcodeSummary[]>(`/variants/${variantId}/barcodes/${barcodeId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
    },
  });
}

/** Issues an internal Code 128 to a variant that has no active barcode. */
export function useGenerateBarcodeMutation(productId: string, variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<ProductBarcodeSummary[]>(`/variants/${variantId}/barcodes/generate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

/** Barcode scan resolution. Shaped for the till, which needs one round trip. */
export function useBarcodeLookupMutation() {
  return useMutation({
    mutationFn: (code: string) =>
      api.get<BarcodeLookupResult>('/variants/barcode-lookup', { params: { code } }),
  });
}

// ---------------------------------------------------------------------------
// Photographs
// ---------------------------------------------------------------------------

export interface PhotoUpload {
  file: File;
  variantId: string | null;
  role: ProductMediaRole;
  primary?: boolean;
}

/** Every photograph change refreshes the product it belongs to and the lists that show its image. */
function usePhotoMutation<TInput, TResult>(
  productId: string,
  mutationFn: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

/** Declares multipart explicitly: with the client's JSON default, axios would turn FormData into JSON and drop the file; the browser then writes the boundary. */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/** The file travels as multipart form data. */
function photoForm(file: File, fields: Record<string, string>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  form.append('file', file);
  return form;
}

export function useUploadPhotoMutation(productId: string) {
  return usePhotoMutation(productId, ({ file, variantId, role, primary }: PhotoUpload) =>
    api.post<ProductMediaSummary>(
      `/products/${productId}/media`,
      photoForm(file, {
        role,
        ...(variantId ? { variantId } : {}),
        ...(primary ? { primary: 'true' } : {}),
      }),
      MULTIPART,
    ),
  );
}

export function useReplacePhotoMutation(productId: string) {
  return usePhotoMutation(productId, ({ mediaId, file }: { mediaId: string; file: File }) =>
    api.post<ProductMediaSummary>(`/product-media/${mediaId}/file`, photoForm(file, {}), MULTIPART),
  );
}

export function useUpdatePhotoMutation(productId: string) {
  return usePhotoMutation(
    productId,
    ({ mediaId, ...changes }: { mediaId: string; role?: ProductMediaRole; primary?: true }) =>
      api.patch<ProductMediaSummary>(`/product-media/${mediaId}`, changes),
  );
}

export function useRemovePhotoMutation(productId: string) {
  return usePhotoMutation(productId, (mediaId: string) =>
    api.delete<void>(`/product-media/${mediaId}`),
  );
}

export function useReorderPhotosMutation(productId: string) {
  return usePhotoMutation(
    productId,
    ({ variantId, mediaIds }: { variantId: string | null; mediaIds: string[] }) =>
      api.put<ProductMediaSummary[]>(`/products/${productId}/media/order`, { variantId, mediaIds }),
  );
}

// ---------------------------------------------------------------------------
// Barcode labels
// ---------------------------------------------------------------------------

/** The content of a print job, built by the server; nothing is fetched while there is nothing to print. */
export function useLabelSheetQuery(request: LabelRequest | null) {
  return useQuery({
    queryKey: [...catalogKeys.all, 'labels', request] as const,
    queryFn: () => api.post<LabelSheet>('/variants/labels', request),
    enabled: request !== null && request.items.length > 0,
    retry: false,
  });
}

/** Issues internal barcodes to the listed variants that have none; the others are left exactly as they are. */
export function useGenerateBarcodesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variantIds: string[]) =>
      api.post<{ generated: { variantId: string; code: string }[]; skipped: string[] }>(
        '/variants/barcodes/generate',
        { variantIds },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}
